import React, { useState, useEffect } from 'react';
import io from 'socket.io-client';

const CriticalAlerts = ({ devices: initialDevices, sites: initialSites, user }) => {
  // Estado local para dispositivos - se actualiza via WebSocket
  const [devices, setDevices] = useState(initialDevices || []);
  const [sites, setSites] = useState(initialSites || []);
  const [criticalAlerts, setCriticalAlerts] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('alertSoundsEnabled');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [previousAlertsCount, setPreviousAlertsCount] = useState(0);
  // Persistir alertas resueltas en localStorage
  const [resolvedAlerts, setResolvedAlerts] = useState(() => {
    const saved = localStorage.getItem('criticalAlertsResolved');
    return saved ? JSON.parse(saved) : [];
  });
  // Estado para alertas aceptadas (reconocidas por el usuario pero aún activas)
  const [acknowledgedAlerts, setAcknowledgedAlerts] = useState(() => {
    const saved = localStorage.getItem('criticalAlertsAcknowledged');
    return saved ? JSON.parse(saved) : [];
  });

  // Guardar resolvedAlerts en localStorage cuando cambia
  useEffect(() => {
    localStorage.setItem('criticalAlertsResolved', JSON.stringify(resolvedAlerts));
  }, [resolvedAlerts]);

  // Guardar acknowledgedAlerts en localStorage cuando cambia
  useEffect(() => {
    localStorage.setItem('criticalAlertsAcknowledged', JSON.stringify(acknowledgedAlerts));
  }, [acknowledgedAlerts]);

  // Función para verificar alertas - IGUAL que MobileDashboard (sin useCallback)
  const checkAlerts = (deviceList, sitesList = sites) => {
    const newAlerts = [];

    console.log(`🚨 CriticalAlerts: ========== CHECKING ALERTS ==========`);
    console.log(`🚨 CriticalAlerts: Checking ${deviceList?.length || 0} devices for alerts`);

    // Filtrar dispositivos según permisos del usuario - UNIFICADO con MobileDashboard
    const getAllowedDevices = () => {
      if (!Array.isArray(deviceList)) return [];

      // Si es admin, puede ver todos los dispositivos
      if (!user || user.role === 'admin') {
        return deviceList;
      }

      // Para otros usuarios, usar devicePermissions (igual que MobileDashboard)
      if (!user.devicePermissions) {
        return [];
      }

      return deviceList.filter(device => {
        const deviceId = device._id;
        const devicePermissions = user.devicePermissions[deviceId];
        return devicePermissions && devicePermissions.allowed;
      });
    };

    // Obtener magnitudes permitidas para un dispositivo - UNIFICADO con MobileDashboard
    const getAllowedMagnitudes = (device) => {
      if (!device.magnitudes) return [];

      // Si es admin, mostrar todas las magnitudes
      if (!user || user.role === 'admin') {
        return device.magnitudes;
      }

      // Para otros usuarios, filtrar según devicePermissions
      if (!user.devicePermissions || !user.devicePermissions[device._id]) {
        return [];
      }

      const devicePerms = user.devicePermissions[device._id];
      if (!devicePerms.magnitudes) {
        return [];
      }

      return device.magnitudes.filter(magnitude =>
        magnitude.showInDashboard !== false &&
        devicePerms.magnitudes.includes(magnitude.name)
      );
    };

    const allowedDevices = getAllowedDevices();
    console.log(`🎯 CriticalAlerts: ${allowedDevices.length} accessible devices`);

    allowedDevices.forEach(device => {
      // No mostrar alertas de dispositivos offline
      if (device.status === 'offline') {
        console.log(`⏭️ CriticalAlerts: Skipping ${device.name} - OFFLINE`);
        return;
      }

      // Usar solo magnitudes permitidas - UNIFICADO con MobileDashboard
      const allowedMagnitudes = getAllowedMagnitudes(device);
      if (allowedMagnitudes.length === 0) {
        console.log(`⏭️ CriticalAlerts: Skipping ${device.name} - No allowed magnitudes`);
        return;
      }

      console.log(`🔍 CriticalAlerts: Checking device ${device.name} with ${allowedMagnitudes.length} magnitudes`);

      allowedMagnitudes.forEach(magnitude => {
        const currentValue = magnitude.currentValue;

        if (currentValue === undefined || currentValue === null) {
          return; // Sin valor, no verificar
        }

        // Manejar magnitudes booleanas - igual que MobileDashboard
        if (magnitude.valueType === 'boolean') {
          const boolValue = currentValue === true || currentValue === 'true' || currentValue === 1 || currentValue === '1';

          console.log(`📊 CriticalAlerts: ${device.name}.${magnitude.label} - BoolValue: ${boolValue} (raw: ${currentValue}), Alert: ${!boolValue}`);

          if (!boolValue) {
            const site = Array.isArray(sitesList)
              ? sitesList.find(s => s._id === device.site || s._id === device.site?._id)
              : null;

            newAlerts.push({
              id: `${device._id}-${magnitude.name}`,
              site: site || { name: 'Sin sitio', _id: 'no-site' },
              device: device,
              magnitude: magnitude,
              value: 'OFF',
              timestamp: device.lastSeen || new Date(),
              severity: 'critical'
            });

            console.log(`🚨 CriticalAlerts: ALERT ADDED (boolean) - ${device.name}.${magnitude.label}: OFF`);
          }
          return;
        }

        // Manejar magnitudes numéricas
        const hasMin = magnitude.minNormalValue !== undefined && magnitude.minNormalValue !== null;
        const hasMax = magnitude.maxNormalValue !== undefined && magnitude.maxNormalValue !== null;

        if (!hasMin && !hasMax) {
          console.log(`  ⏭️ ${device.name}.${magnitude.label} - No ranges defined (min: ${magnitude.minNormalValue}, max: ${magnitude.maxNormalValue})`);
          return; // Sin rangos definidos, no verificar
        }

        const numValue = typeof currentValue === 'string' ? parseFloat(currentValue) : currentValue;

        if (isNaN(numValue)) {
          return;
        }

        // Verificar si está fuera de rango - igual que MobileDashboard
        const isBelowMin = hasMin && numValue < magnitude.minNormalValue;
        const isAboveMax = hasMax && numValue > magnitude.maxNormalValue;
        const isOutOfRange = isBelowMin || isAboveMax;

        console.log(`📊 CriticalAlerts: ${device.name}.${magnitude.label} - Value: ${numValue}, Min: ${magnitude.minNormalValue}, Max: ${magnitude.maxNormalValue}, OutOfRange: ${isOutOfRange}`);

        if (isOutOfRange) {
          const site = Array.isArray(sitesList)
            ? sitesList.find(s => s._id === device.site || s._id === device.site?._id)
            : null;

          newAlerts.push({
            id: `${device._id}-${magnitude.name}`,
            site: site || { name: 'Sin sitio', _id: 'no-site' },
            device: device,
            magnitude: magnitude,
            value: numValue,
            timestamp: device.lastSeen || new Date(),
            severity: getSeverityLevel(magnitude, numValue)
          });

          console.log(`🚨 CriticalAlerts: ALERT ADDED - ${device.name}.${magnitude.label}: ${numValue}`);
        }
      });
    });

    // Ordenar por timestamp (más nuevas primero)
    newAlerts.sort((a, b) => {
      return new Date(b.timestamp) - new Date(a.timestamp);
    });

    console.log(`🚨 CriticalAlerts: FINAL RESULT - Found ${newAlerts.length} alerts`);

    // Detectar alertas que se resolvieron - igual que MobileDashboard
    setCriticalAlerts(prevAlerts => {
      const newAlertIds = new Set(newAlerts.map(a => a.id));
      const prevAlertIds = new Set(prevAlerts.map(a => a.id));

      // Alertas que desaparecieron (se resolvieron)
      const resolvedNow = prevAlerts.filter(a => !newAlertIds.has(a.id));

      // NUEVO: Alertas que volvieron a activarse (estaban resueltas y ahora están activas)
      const reactivatedAlerts = newAlerts.filter(a => !prevAlertIds.has(a.id));

      if (resolvedNow.length > 0) {
        console.log(`✅ CriticalAlerts: ${resolvedNow.length} alertas se resolvieron`);

        // Limpiar las alertas resueltas del estado de "aceptadas"
        const resolvedIds = resolvedNow.map(a => a.id);
        setAcknowledgedAlerts(prev => prev.filter(id => !resolvedIds.includes(id)));

        setResolvedAlerts(prev => {
          const existingIds = new Set(prev.map(a => a.id));
          const newResolved = resolvedNow
            .filter(a => !existingIds.has(a.id))
            .map(a => ({ ...a, resolvedAt: new Date(), isResolved: true }));
          return [...prev, ...newResolved];
        });
      }

      // NUEVO: Cuando una alerta se reactiva, asegurarse de que no esté en "aceptadas"
      // Esto cubre el caso donde una alerta fue aceptada, resuelta, y vuelve a activarse
      if (reactivatedAlerts.length > 0) {
        console.log(`🔴 CriticalAlerts: ${reactivatedAlerts.length} alertas se reactivaron`);
        const reactivatedIds = reactivatedAlerts.map(a => a.id);

        // Remover de alertas aceptadas (deben aparecer como activas/rojas de nuevo)
        setAcknowledgedAlerts(prev => {
          const updated = prev.filter(id => !reactivatedIds.includes(id));
          if (updated.length !== prev.length) {
            console.log(`🔄 CriticalAlerts: Removiendo ${prev.length - updated.length} alertas del estado "aceptadas" porque se reactivaron`);
          }
          return updated;
        });

        // Remover de alertas resueltas (ya no están resueltas)
        setResolvedAlerts(prev => {
          const updated = prev.filter(a => !reactivatedIds.includes(a.id));
          if (updated.length !== prev.length) {
            console.log(`🔄 CriticalAlerts: Removiendo ${prev.length - updated.length} alertas del estado "resueltas" porque se reactivaron`);
          }
          return updated;
        });
      }

      return newAlerts;
    });

    setLastUpdate(new Date());
  };

  // Helper para calcular severidad
  const getSeverityLevel = (magnitude, value) => {
    const min = magnitude.minNormalValue;
    const max = magnitude.maxNormalValue;

    if (min !== null && min !== undefined && max !== null && max !== undefined) {
      const range = max - min;
      if (range > 0) {
        const deviation = Math.max(
          Math.abs(value - min) / range,
          Math.abs(value - max) / range
        );

        if (deviation > 0.5) return 'critical';
        if (deviation > 0.2) return 'warning';
      }
    }
    return 'critical';
  };

  // Determina si una alerta corresponde a un dispositivo/magnitud que el usuario
  // TIENE permitido ver. Se aplica a las alertas RESUELTAS que se leen de localStorage:
  // esas persisten entre sesiones y, al cambiar de usuario en el mismo navegador,
  // podrían mostrar dispositivos de otros clientes (fuga cross-tenant). Las alertas
  // activas ya se filtran en checkAlerts(); esto cubre las resueltas/aceptadas.
  const isAlertAllowed = (alert) => {
    if (!user || user.role === 'admin') return true;
    const deviceId = alert?.device?._id;
    if (!deviceId) return false;
    const perms = user.devicePermissions?.[deviceId];
    if (!perms || !perms.allowed) return false;
    const magName = alert?.magnitude?.name;
    if (perms.magnitudes && magName && !perms.magnitudes.includes(magName)) return false;
    return true;
  };

  // Al montar o cambiar de usuario, purgar de localStorage las alertas resueltas/aceptadas
  // que no pertenezcan a los dispositivos permitidos (limpieza persistente).
  useEffect(() => {
    if (!user) return;
    setResolvedAlerts(prev => {
      const filtered = prev.filter(isAlertAllowed);
      return filtered.length === prev.length ? prev : filtered;
    });
    setAcknowledgedAlerts(prev => {
      const filtered = prev.filter(id => {
        if (user.role === 'admin') return true;
        const deviceId = String(id).split('-')[0]; // los IDs son `${deviceId}-${magName}`
        return user.devicePermissions?.[deviceId]?.allowed === true;
      });
      return filtered.length === prev.length ? prev : filtered;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // useEffect PRINCIPAL - carga inicial + WebSocket - IGUAL que MobileDashboard
  useEffect(() => {
    console.log('🔌 CriticalAlerts: Setting up...');

    // Actualizar desde props iniciales
    if (initialDevices && initialDevices.length > 0) {
      setDevices(initialDevices);
      checkAlerts(initialDevices, initialSites);
    }
    if (initialSites && initialSites.length > 0) {
      setSites(initialSites);
    }

    // Configurar WebSocket
    const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
    const socket = io(apiUrl, { auth: { token: localStorage.getItem('token') } });

    socket.on('connect', () => {
      console.log('✅ CriticalAlerts: Connected to WebSocket');
    });

    socket.on('device-update', (data) => {
      console.log('📡 CriticalAlerts: device-update received:', data);
      const { deviceId, deviceIdMqtt, magnitudes, currentState, status, lastSeen } = data;

      setDevices(prevDevices => {
        const updatedDevices = prevDevices.map(device => {
          const isMatch = device._id === deviceId ||
                          device._id === String(deviceId) ||
                          device.deviceId === deviceIdMqtt;

          if (isMatch) {
            const updatedDevice = {
              ...device,
              status: status || device.status,
              lastSeen: lastSeen || device.lastSeen
            };

            if (currentState) {
              updatedDevice.currentState = { ...device.currentState, ...currentState };
            }

            if (magnitudes && Array.isArray(magnitudes)) {
              updatedDevice.magnitudes = magnitudes;
              console.log('📊 CriticalAlerts: Magnitudes updated for', device.name);
            }

            return updatedDevice;
          }
          return device;
        });

        // Re-verificar alertas con los dispositivos actualizados - CLAVE!
        console.log('🔄 CriticalAlerts: Re-checking alerts after device update...');
        checkAlerts(updatedDevices);

        return updatedDevices;
      });
    });

    // Auto-refresh cada 30 segundos como fallback - igual que MobileDashboard
    const interval = setInterval(() => {
      setDevices(currentDevices => {
        checkAlerts(currentDevices);
        return currentDevices;
      });
    }, 30000);

    return () => {
      console.log('🧹 CriticalAlerts: Disconnecting WebSocket...');
      socket.disconnect();
      clearInterval(interval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo ejecutar una vez al montar - igual que MobileDashboard

  // Actualizar cuando cambian los props
  useEffect(() => {
    if (initialDevices && initialDevices.length > 0) {
      setDevices(initialDevices);
      checkAlerts(initialDevices, initialSites);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDevices]);

  useEffect(() => {
    if (initialSites && initialSites.length > 0) {
      setSites(initialSites);
    }
  }, [initialSites]);

  // Funciones para generar sonidos naturales
  const playSound = (type) => {
    if (!soundEnabled) return;
    
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const masterGain = audioContext.createGain();
      masterGain.connect(audioContext.destination);
      
      if (type === 'alert') {
        // Sonido de alerta natural: simulando campana suave con armónicos
        const createBellTone = (freq, gain, delay = 0) => {
          // Oscilador principal
          const osc1 = audioContext.createOscillator();
          const gain1 = audioContext.createGain();
          osc1.type = 'sine';
          osc1.frequency.setValueAtTime(freq, audioContext.currentTime + delay);
          
          // Armónicos para sonido más natural
          const osc2 = audioContext.createOscillator();
          const gain2 = audioContext.createGain();
          osc2.type = 'sine';
          osc2.frequency.setValueAtTime(freq * 2.5, audioContext.currentTime + delay); // Quinta
          
          const osc3 = audioContext.createOscillator();
          const gain3 = audioContext.createGain();
          osc3.type = 'triangle';
          osc3.frequency.setValueAtTime(freq * 4, audioContext.currentTime + delay); // Octava
          
          // Conectar osciladores
          osc1.connect(gain1);
          osc2.connect(gain2);
          osc3.connect(gain3);
          gain1.connect(masterGain);
          gain2.connect(masterGain);
          gain3.connect(masterGain);
          
          // Envolvente natural (attack, decay, sustain, release)
          const startTime = audioContext.currentTime + delay;
          
          // Oscilador principal
          gain1.gain.setValueAtTime(0, startTime);
          gain1.gain.linearRampToValueAtTime(gain * 0.8, startTime + 0.05); // Attack rápido
          gain1.gain.exponentialRampToValueAtTime(gain * 0.6, startTime + 0.2); // Decay
          gain1.gain.exponentialRampToValueAtTime(0.001, startTime + 1.2); // Release largo
          
          // Armónico 1 (más suave)
          gain2.gain.setValueAtTime(0, startTime);
          gain2.gain.linearRampToValueAtTime(gain * 0.3, startTime + 0.08);
          gain2.gain.exponentialRampToValueAtTime(gain * 0.2, startTime + 0.3);
          gain2.gain.exponentialRampToValueAtTime(0.001, startTime + 1.0);
          
          // Armónico 2 (aún más suave)
          gain3.gain.setValueAtTime(0, startTime);
          gain3.gain.linearRampToValueAtTime(gain * 0.15, startTime + 0.1);
          gain3.gain.exponentialRampToValueAtTime(gain * 0.1, startTime + 0.4);
          gain3.gain.exponentialRampToValueAtTime(0.001, startTime + 0.8);
          
          // Iniciar osciladores
          osc1.start(startTime);
          osc2.start(startTime);
          osc3.start(startTime);
          osc1.stop(startTime + 1.2);
          osc2.stop(startTime + 1.0);
          osc3.stop(startTime + 0.8);
        };
        
        // Crear acorde de campanas en Do mayor (C-E-G)
        createBellTone(523.25, 0.15, 0);    // C5
        createBellTone(659.25, 0.12, 0.1);  // E5
        createBellTone(783.99, 0.1, 0.2);   // G5
        
      } else if (type === 'resolved') {
        // Sonido de resolución: arpegio descendente suave como lluvia
        const createDroplet = (freq, gain, delay = 0) => {
          const osc = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          const filter = audioContext.createBiquadFilter();
          
          osc.type = 'sine';
          osc.frequency.setValueAtTime(freq, audioContext.currentTime + delay);
          filter.type = 'lowpass';
          filter.frequency.setValueAtTime(2000, audioContext.currentTime + delay);
          filter.Q.setValueAtTime(1, audioContext.currentTime + delay);
          
          osc.connect(filter);
          filter.connect(gainNode);
          gainNode.connect(masterGain);
          
          const startTime = audioContext.currentTime + delay;
          
          // Envolvente de gota de agua
          gainNode.gain.setValueAtTime(0, startTime);
          gainNode.gain.linearRampToValueAtTime(gain, startTime + 0.02); // Attack muy rápido
          gainNode.gain.exponentialRampToValueAtTime(gain * 0.3, startTime + 0.1);
          gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + 0.6); // Decay natural
          
          // Modulación sutil de frecuencia
          osc.frequency.linearRampToValueAtTime(freq * 0.95, startTime + 0.3);
          
          osc.start(startTime);
          osc.stop(startTime + 0.6);
        };
        
        // Crear secuencia de gotas (arpegio descendente)
        createDroplet(783.99, 0.2, 0);     // G5
        createDroplet(659.25, 0.18, 0.15); // E5
        createDroplet(523.25, 0.16, 0.3);  // C5
        createDroplet(392.00, 0.14, 0.45); // G4
      }
      
      // Control de volumen maestro
      masterGain.gain.setValueAtTime(0.25, audioContext.currentTime);
      
    } catch (error) {
      console.warn('Audio no disponible:', error);
    }
  };

  // Efecto para detectar cambios en alertas y reproducir sonidos
  useEffect(() => {
    if (previousAlertsCount === 0) {
      // Inicialización, no reproducir sonido
      setPreviousAlertsCount(criticalAlerts.length);
      return;
    }

    const currentCount = criticalAlerts.length;
    
    if (currentCount > previousAlertsCount) {
      // Nueva alerta aparecida
      playSound('alert');
      console.log('🚨 Nueva alerta crítica detectada');
    } else if (currentCount < previousAlertsCount) {
      // Alerta resuelta
      playSound('resolved');
      console.log('✅ Alerta crítica resuelta');
    }

    setPreviousAlertsCount(currentCount);
  }, [criticalAlerts.length, previousAlertsCount, soundEnabled]);

  const toggleSound = () => {
    const newSoundState = !soundEnabled;
    setSoundEnabled(newSoundState);
    localStorage.setItem('alertSoundsEnabled', JSON.stringify(newSoundState));

    // Reproducir sonido de prueba cuando se activa
    if (newSoundState) {
      setTimeout(() => playSound('alert'), 100);
    }
  };

  const formatValue = (alert) => {
    const { magnitude, value } = alert;

    if (magnitude.valueType === 'boolean') {
      // El valor ya viene formateado como 'OFF' desde checkAlerts
      // Si es string 'OFF' o 'ON', devolverlo tal cual
      if (value === 'OFF' || value === 'ON') {
        return value;
      }
      // Si es booleano real, convertir
      return value ? 'ON' : 'OFF';
    }

    if (typeof value === 'number') {
      const decimals = magnitude.decimals || 1;
      return `${value.toFixed(decimals)} ${magnitude.unit || ''}`;
    }

    return value.toString();
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return '#ef4444';
      case 'warning': return '#f59e0b';
      case 'info': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return '🚨';
      case 'warning': return '⚠️';
      case 'info': return 'ℹ️';
      default: return '❓';
    }
  };

  const getTimeSinceUpdate = (timestamp) => {
    const now = new Date();
    const diff = now - new Date(timestamp);
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    return `${minutes}m`;
  };

  // Si el usuario no tiene permisos de dispositivos, no mostrar nada
  if (user && user.role !== 'admin' && (!user.devicePermissions || Object.keys(user.devicePermissions).length === 0)) {
    return null;
  }

  // Alertas resueltas visibles = sólo las de dispositivos permitidos (filtro cross-tenant)
  const visibleResolvedAlerts = resolvedAlerts.filter(isAlertAllowed);

  return (
    <div style={{
      background: 'linear-gradient(145deg, #0f172a, #1e293b)',
      borderRadius: '20px',
      padding: '2rem',
      margin: '1rem',
      boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(239, 68, 68, 0.3)'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
        paddingBottom: '1.5rem',
        borderBottom: '2px solid rgba(239, 68, 68, 0.3)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1.5rem'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '1.3rem',
            fontWeight: '700',
            color: '#ffffff',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <span style={{
              background: 'linear-gradient(135deg, #ef4444, #dc2626)',
              borderRadius: '8px',
              padding: '0.3rem',
              fontSize: '1.2rem',
              boxShadow: '0 2px 8px rgba(239, 68, 68, 0.4)'
            }}>
              🚨
            </span>
            Alertas Críticas
          </h2>
          <p style={{
            margin: 0,
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '0.9rem'
          }}>
            {(() => {
              const activeCount = criticalAlerts.length;
              const resolvedCount = visibleResolvedAlerts.length;

              if (activeCount === 0 && resolvedCount > 0) {
                return (
                  <span style={{ color: '#10b981' }}>
                    {resolvedCount} alerta{resolvedCount !== 1 ? 's' : ''} resuelta{resolvedCount !== 1 ? 's' : ''}
                  </span>
                );
              }

              if (activeCount === 0 && resolvedCount === 0) {
                return 'Sin alertas activas';
              }

              return (
                <>
                  {activeCount} alerta{activeCount !== 1 ? 's' : ''} activa{activeCount !== 1 ? 's' : ''}
                  {resolvedCount > 0 && (
                    <span style={{ color: '#10b981' }}>
                      {' '}| {resolvedCount} resuelta{resolvedCount !== 1 ? 's' : ''}
                    </span>
                  )}
                </>
              );
            })()}
          </p>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          fontSize: '0.85rem',
          color: 'rgba(255, 255, 255, 0.6)'
        }}>
          <button
            onClick={toggleSound}
            style={{
              padding: '0.5rem',
              background: soundEnabled 
                ? 'linear-gradient(135deg, #10b981, #059669)' 
                : 'linear-gradient(135deg, #6b7280, #4b5563)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
              transition: 'all 0.2s ease',
              boxShadow: soundEnabled 
                ? '0 2px 8px rgba(16, 185, 129, 0.4)' 
                : '0 2px 8px rgba(107, 114, 128, 0.4)'
            }}
            title={soundEnabled ? 'Sonidos activados - Clic para silenciar' : 'Sonidos silenciados - Clic para activar'}
          >
            {soundEnabled ? '🔊' : '🔇'}
            <span style={{ fontSize: '0.75rem' }}>
              {soundEnabled ? 'ON' : 'OFF'}
            </span>
          </button>

          🔄 {lastUpdate.toLocaleTimeString('es-AR', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          })}
        </div>
      </div>

      {/* Lista de alertas */}
      {(criticalAlerts.length > 0 || visibleResolvedAlerts.length > 0) ? (() => {
        // Ordenar: activas primero (por severidad), resueltas al final
        const sortedAlerts = [...criticalAlerts, ...visibleResolvedAlerts];

        return (
        <div style={{
          display: 'grid',
          gap: '0.5rem'
        }}>
          {sortedAlerts.map(alert => {
            const isResolved = alert.isResolved === true;
            const isAcknowledged = !isResolved && acknowledgedAlerts.includes(alert.id);

            // Determinar estilos según estado
            let backgroundColor, borderColor, textColor, valueColor;
            if (isResolved) {
              backgroundColor = 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(5, 150, 105, 0.15))';
              borderColor = '#10b981';
              textColor = '#10b981';
              valueColor = '#10b981';
            } else if (isAcknowledged) {
              // Colores grises para alertas aceptadas
              backgroundColor = 'linear-gradient(135deg, rgba(107, 114, 128, 0.15), rgba(75, 85, 99, 0.15))';
              borderColor = '#6b7280';
              textColor = '#9ca3af';
              valueColor = '#9ca3af';
            } else {
              backgroundColor = 'linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(220, 38, 38, 0.1))';
              borderColor = getSeverityColor(alert.severity);
              textColor = '#ffffff';
              valueColor = getSeverityColor(alert.severity);
            }

            return (
            <div
              key={alert.id}
              style={{
                background: backgroundColor,
                borderRadius: '8px',
                padding: '0.75rem',
                border: `1px solid ${borderColor}`,
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                transition: 'all 0.2s ease',
                opacity: isAcknowledged ? 0.7 : 1
              }}
            >
              {/* Icono de severidad */}
              <div style={{
                fontSize: '1.5rem',
                animation: !isResolved && !isAcknowledged && alert.severity === 'critical' ? 'pulse 2s infinite' : 'none',
                minWidth: '30px',
                filter: isAcknowledged ? 'grayscale(100%)' : 'none'
              }}>
                {isResolved ? '✅' : isAcknowledged ? '✓' : getSeverityIcon(alert.severity)}
              </div>

              {/* Información en una sola línea */}
              <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                fontSize: '0.9rem'
              }}>
                {/* Badge RESUELTA para alertas resueltas */}
                {isResolved && (
                  <div style={{
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    color: 'white',
                    padding: '0.2rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.7rem',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)'
                  }}>
                    RESUELTA
                  </div>
                )}

                {/* Badge ACEPTADA para alertas aceptadas */}
                {isAcknowledged && (
                  <div style={{
                    background: 'linear-gradient(135deg, #6b7280, #4b5563)',
                    color: 'white',
                    padding: '0.2rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.7rem',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    boxShadow: '0 2px 4px rgba(107, 114, 128, 0.3)'
                  }}>
                    ACEPTADA
                  </div>
                )}

                {/* Sitio y dispositivo */}
                <div style={{
                  color: isAcknowledged ? 'rgba(156, 163, 175, 0.8)' : 'rgba(255, 255, 255, 0.7)',
                  minWidth: '200px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  🏢 {alert.site.name} • 📱 {alert.device.name}
                </div>

                {/* Magnitud */}
                <div style={{
                  fontWeight: '700',
                  color: textColor,
                  minWidth: '120px',
                  textAlign: 'left',
                  fontSize: '1.1rem'
                }}>
                  {alert.magnitude.icon || '📊'} {alert.magnitude.label}
                </div>

                {/* Fecha y hora */}
                <div style={{
                  fontSize: '0.8rem',
                  color: isAcknowledged ? 'rgba(156, 163, 175, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                  minWidth: '90px',
                  textAlign: 'left'
                }}>
                  🕒 {new Date(alert.timestamp).toLocaleString('es-AR', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>

                {/* Valor crítico (o último valor antes de resolver) */}
                <div style={{
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  color: valueColor,
                  minWidth: '80px',
                  textAlign: 'center'
                }}>
                  {formatValue(alert)}
                </div>

                {/* Estado online/offline */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  fontSize: '0.75rem',
                  minWidth: '60px'
                }}>
                  <div style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: isAcknowledged ? '#6b7280' : (alert.device.status === 'online' ? '#10b981' : '#6b7280')
                  }} />
                  <span style={{
                    color: isAcknowledged ? '#6b7280' : (alert.device.status === 'online' ? '#10b981' : '#6b7280')
                  }}>
                    {alert.device.status === 'online' ? 'Online' : 'Offline'}
                  </span>
                </div>

                {/* Botón Aceptar - para alertas activas NO aceptadas */}
                {!isResolved && !isAcknowledged && (
                  <button
                    onClick={() => {
                      setAcknowledgedAlerts(prev => [...prev, alert.id]);
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #6b7280, #4b5563)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '0.4rem 0.75rem',
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      marginLeft: '0.5rem',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 6px rgba(107, 114, 128, 0.3)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, #4b5563, #374151)';
                      e.target.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, #6b7280, #4b5563)';
                      e.target.style.transform = 'scale(1)';
                    }}
                  >
                    Aceptar
                  </button>
                )}

                {/* Botón Aceptar - para alertas resueltas (para quitarlas de la lista) */}
                {isResolved && (
                  <button
                    onClick={() => {
                      // Remover de resolvedAlerts
                      setResolvedAlerts(prev => prev.filter(a => a.id !== alert.id));
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #10b981, #059669)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '0.4rem 0.75rem',
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      marginLeft: '0.5rem',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 6px rgba(16, 185, 129, 0.3)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, #059669, #047857)';
                      e.target.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, #10b981, #059669)';
                      e.target.style.transform = 'scale(1)';
                    }}
                  >
                    Aceptar
                  </button>
                )}
              </div>
            </div>
          );
          })}
        </div>
      );
      })() : (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          color: 'rgba(255, 255, 255, 0.6)'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
          <div style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>
            ¡Todo está funcionando correctamente!
          </div>
          <div style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.5)' }}>
            No hay magnitudes fuera de sus rangos normales
          </div>
        </div>
      )}
    </div>
  );
};

export default CriticalAlerts;